import { useEffect, useRef } from "react"

// Animated mini-mockup for the Collaborate feature row.
// Writer-centric story (Sarah Kim). Methodology row animates through the
// lifecycle while the surrounding rows stay static for context:
//   Unassigned → Assign Sarah → In progress → Send to Jane for review
//   → Comment from Jane → Resolve → ✓ Complete
// The notification bell badge increments + pulses on each handoff.
// No dropdown — minimalist by design.
// Styles live under `.cb-*` classes in src/index.css (scoped to .landing-page).

// Declared full-loop duration — the feature browser reads it to time how long
// the Collaborate tab stays active (so the demo isn't cut short).
export const COLLAB_CYCLE_MS = 16500

const COMMENT_TEXT = "Add the ISO 45001 audit reference here."

export function CollaborateVisual() {
  const rootRef = useRef<HTMLDivElement>(null)
  const bellRef = useRef<HTMLDivElement>(null)
  const badgeRef = useRef<HTMLSpanElement>(null)
  const focalAvatarsRef = useRef<HTMLDivElement>(null)
  const focalStatusRef = useRef<HTMLSpanElement>(null)
  const commentRef = useRef<HTMLDivElement>(null)
  const commentTextRef = useRef<HTMLDivElement>(null)
  const resolveBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    let timers: ReturnType<typeof setTimeout>[] = []
    let isPlaying = false
    let isInView = false

    const tt = (fn: () => void, ms: number) => {
      const t = setTimeout(fn, ms)
      timers.push(t)
      return t
    }
    const clear = () => {
      timers.forEach((t) => clearTimeout(t))
      timers = []
    }

    function bumpBell(n: number) {
      const b = badgeRef.current
      const bell = bellRef.current
      if (!b || !bell) return
      b.textContent = String(n)
      b.classList.add("show")
      // restart pulse animation
      b.classList.remove("pulse")
      bell.classList.remove("ring")
      void b.offsetWidth
      b.classList.add("pulse")
      bell.classList.add("ring")
    }

    function setStatus(label: string, kind?: "in-progress" | "in-review" | "complete") {
      const el = focalStatusRef.current
      if (!el) return
      el.textContent = label
      el.className = "cb-status" + (kind ? " " + kind : "")
    }

    function makeAvatar(initials: string, color: string, useAppear: boolean): HTMLSpanElement {
      const span = document.createElement("span")
      span.className = "cb-avatar" + (useAppear ? " appear" : "")
      span.style.background = color
      span.textContent = initials
      return span
    }

    function typeInto(el: HTMLElement, text: string, delay: number, done?: () => void) {
      let i = 0
      el.classList.add("typing")
      function step() {
        if (!isPlaying) return
        if (i < text.length) {
          el.textContent = text.slice(0, i + 1)
          i++
          tt(step, delay)
        } else {
          el.classList.remove("typing")
          if (done) done()
        }
      }
      step()
    }

    function reset() {
      clear()
      const badge = badgeRef.current
      const bell = bellRef.current
      const focalAvatars = focalAvatarsRef.current
      const comment = commentRef.current
      const commentText = commentTextRef.current
      const resolveBtn = resolveBtnRef.current

      if (badge) {
        badge.classList.remove("show", "pulse")
        badge.textContent = "0"
      }
      bell?.classList.remove("ring")
      if (focalAvatars) {
        focalAvatars.innerHTML = '<span class="cb-avatar unassigned">+</span>'
      }
      setStatus("Unassigned")
      comment?.classList.remove("show", "actions-show", "resolved")
      if (commentText) commentText.textContent = ""
      resolveBtn?.classList.remove("pressed")
    }

    function play() {
      if (!isPlaying) return
      reset()

      // 1) Assign Sarah Kim
      tt(() => {
        const focalAvatars = focalAvatarsRef.current
        if (focalAvatars) {
          focalAvatars.innerHTML = ""
          focalAvatars.appendChild(makeAvatar("SK", "#7A2E3D", true))
        }
        setStatus("In progress", "in-progress")
        bumpBell(1)
      }, 1200)

      // 2) Send for review — Jane Tan joins
      tt(() => {
        focalAvatarsRef.current?.appendChild(makeAvatar("JT", "#A16207", true))
        setStatus("In review", "in-review")
        bumpBell(2)
      }, 4000)

      // 3) Comment balloon from Jane
      tt(() => {
        commentRef.current?.classList.add("show")
        const commentText = commentTextRef.current
        if (commentText) {
          typeInto(commentText, COMMENT_TEXT, 22, () => {
            commentRef.current?.classList.add("actions-show")
            bumpBell(3)
          })
        }
      }, 6000)

      // 4) Resolve comment
      tt(() => {
        resolveBtnRef.current?.classList.add("pressed")
        tt(() => {
          commentRef.current?.classList.add("resolved")
          tt(() => commentRef.current?.classList.remove("show", "actions-show", "resolved"), 350)
        }, 350)
      }, 10500)

      // 5) Mark complete
      tt(() => {
        setStatus("Complete", "complete")
        bumpBell(4)
      }, 11800)

      // 6) Hold and restart
      tt(() => play(), COLLAB_CYCLE_MS)
    }

    function start() {
      if (isPlaying || reduced) return
      isPlaying = true
      play()
    }
    function stop() {
      isPlaying = false
      clear()
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        const wasInView = isInView
        isInView = entry.isIntersecting
        if (isInView && !wasInView) start()
        else if (!isInView && wasInView) stop()
      },
      { threshold: 0.35 },
    )
    io.observe(root)

    return () => {
      io.disconnect()
      stop()
    }
  }, [])

  return (
    <div ref={rootRef}>
      <div className="cb-frame">
        <header className="cb-head">
          <div>
            <span className="cb-title">Westbrook Highway Upgrade</span>
            <span className="cb-title-sub">5 sections</span>
          </div>
          <div ref={bellRef} className="cb-bell">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
            <span ref={badgeRef} className="cb-badge">0</span>
          </div>
        </header>

        <div className="cb-body">
          {/* Static: complete */}
          <div className="cb-row">
            <div className="cb-row-name">
              <span className="cb-row-num">1.</span>
              <span className="cb-row-title">Executive Summary</span>
            </div>
            <div className="cb-avatars">
              <span className="cb-avatar" style={{ background: "#7A2E3D" }}>SK</span>
            </div>
            <span className="cb-status complete">Complete</span>
          </div>

          {/* Focal row */}
          <div className="cb-row focal cb-comment-anchor">
            <div className="cb-row-name">
              <span className="cb-row-num">2.</span>
              <span className="cb-row-title">Methodology</span>
            </div>
            <div ref={focalAvatarsRef} className="cb-avatars">
              <span className="cb-avatar unassigned">+</span>
            </div>
            <span ref={focalStatusRef} className="cb-status">Unassigned</span>

            {/* Comment balloon */}
            <div ref={commentRef} className="cb-comment">
              <div className="cb-comment-head">
                <span className="cb-avatar" style={{ background: "#A16207" }}>JT</span>
                <span className="cb-comment-author">Jane Tan</span>
                <span>· Reviewer</span>
              </div>
              <div ref={commentTextRef} className="cb-comment-text" />
              <div className="cb-comment-actions">
                <button ref={resolveBtnRef} type="button" className="cb-comment-btn resolve">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Resolve
                </button>
                <button type="button" className="cb-comment-btn">Reply</button>
              </div>
            </div>
          </div>

          {/* Static: in progress */}
          <div className="cb-row">
            <div className="cb-row-name">
              <span className="cb-row-num">3.</span>
              <span className="cb-row-title">Project Experience</span>
            </div>
            <div className="cb-avatars">
              <span className="cb-avatar" style={{ background: "#7A2E3D" }}>SK</span>
            </div>
            <span className="cb-status in-progress">In progress</span>
          </div>

          {/* Static: unassigned */}
          <div className="cb-row">
            <div className="cb-row-name">
              <span className="cb-row-num">4.</span>
              <span className="cb-row-title">Pricing &amp; Schedule</span>
            </div>
            <div className="cb-avatars">
              <span className="cb-avatar unassigned">+</span>
            </div>
            <span className="cb-status">Unassigned</span>
          </div>

          {/* Static: in review */}
          <div className="cb-row">
            <div className="cb-row-name">
              <span className="cb-row-num">5.</span>
              <span className="cb-row-title">Compliance</span>
            </div>
            <div className="cb-avatars">
              <span className="cb-avatar" style={{ background: "#7B61FF" }}>MR</span>
              <span className="cb-avatar" style={{ background: "#A16207" }}>JT</span>
            </div>
            <span className="cb-status in-review">In review</span>
          </div>
        </div>
      </div>
    </div>
  )
}
